import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import type { BaseQLFieldDefinition } from '@adornis/baseql/metadata/types.js';
import type { BuddyUserMentee, BuddyUserMentor } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { MatchingLanguage } from '@adornis/bh-matching-user/entities/auto-matching-fields.js';
import '@adornis/buchungsheld-theme/components/tr-checkbox.js';
import '@adornis/buchungsheld-theme/components/tr-dropdown-selection.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import '@adornis/buchungsheld-theme/components/tr-input.js';
import '@adornis/buchungsheld-theme/components/tr-tooltip.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import type { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { differenceInYears } from 'date-fns';
import einverstandiserklärung from 'file:../client/assets/Einverständniserklärung für die Teilnahme am Projekt.pdf';
import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { MatchingHautnahMenteeUser, MatchingHautnahMentorUser } from '../entities/matching-user.js';
import type { TranslationDictionaryMatching } from '../translations.js';

// The mappings only need the document and single-field binding, regardless of the form implementation.
interface MatchingFieldForm {
  document: Maybe<BuddyUserMentee | BuddyUserMentor>;
  field(key: string): Renderable;
}

type MatchingTranslation = Pick<TranslationController<TranslationDictionaryMatching>, 'translate'>;

export function mentorCustomFieldMapping(
  field: BaseQLFieldDefinition,
  form: MatchingFieldForm,
  translation: MatchingTranslation = clientTranslator,
  parent?: ChemistryLitElement,
) {
  switch (field.name) {
    case 'dataPrivacyConsent':
      const trans = translation.translate('field', field.name);
      return html`
        <tr-checkbox
          .label=${html`${unsafeHTML(
            trans
              .replace('Datenschutzerklärung', '<a href="/data-privacy" target="_blank"> Datenschutzerklärung</a>')
              .replace('privacy agreement', '<a href="/data-privacy" target="_blank"> privacy agreement</a>'),
          )}`}
          ${form.field(field.name)}
          ${css({
            gridColumn: '1 / -1',
          })}
        ></tr-checkbox>
      `;
    case 'age':
      return html``;
    case 'nativeLanguage':
      // sort options by key-translation
      const selectables1 = Object.values(MatchingLanguage)
        .filter(key => typeof key === 'string')
        .map(key => ({
          key,
          keyTranslation: translation.translate('enums', key),
        }))
        .sort(({ keyTranslation: keyA, key: _keyA }, { keyTranslation: keyB, key: _keyB }) => {
          if (_keyA === MatchingLanguage.otherLanguage) return 1;
          if (_keyB === MatchingLanguage.otherLanguage) return -1;
          return keyA.localeCompare(keyB);
        })
        .map(({ key }) => key);

      return html`<tr-dropdown-selection
        placeholder=${translation.translate('field', field.name)}
        .selectables=${selectables1}
        .renderString=${(value: MatchingLanguage) => translation.translate('enums', value)}
        info=${translation.translate('field_description', field.name)}
        @value-picked=${(e: ValueEvent<MatchingLanguage>) => {
          if (form.document instanceof MatchingHautnahMentorUser) form.document.nativeLanguage = e.detail.value;
        }}
      ></tr-dropdown-selection>`;

    case 'foreignLanguages':
      // sort options by key-translation
      const selectables2 = Object.values(MatchingLanguage)
        .filter(key => typeof key === 'string')
        .map(key => ({
          key,
          keyTranslation: translation.translate('enums', key),
        }))
        .sort(({ keyTranslation: keyA, key: _keyA }, { keyTranslation: keyB, key: _keyB }) => {
          if (_keyA === MatchingLanguage.otherLanguage) return 1;
          if (_keyB === MatchingLanguage.otherLanguage) return -1;
          return keyA.localeCompare(keyB);
        })
        .map(({ key }) => key);

      return html`<tr-dropdown-multiselection
        placeholder=${translation.translate('field', field.name)}
        .selectables=${selectables2}
        .renderString=${(value: MatchingLanguage) => translation.translate('enums', value)}
        info=${translation.translate('field_description', field.name)}
        @value-picked=${(e: ValueEvent<MatchingLanguage[]>) => {
          if (form.document instanceof MatchingHautnahMentorUser) form.document.foreignLanguages = e.detail.value;
        }}
      ></tr-dropdown-multiselection>`;

    case 'numberOfMentees':
      return html`<tr-dropdown-selection
        placeholder=${translation.translate('field', field.name)}
        .selectables=${[2, 4]}
        .renderString=${(value: number) => (value === 2 ? '1-2 Personen' : '3-4 Personen')}
        info=${translation.translate('field_description', field.name)}
        @value-picked=${(e: ValueEvent<number>) => {
          if (form.document instanceof MatchingHautnahMentorUser) form.document.numberOfMentees = e.detail.value;
        }}
      ></tr-dropdown-selection>`;

    default:
      return undefined;
  }
}

export function menteeCustomFieldMapping(
  field: BaseQLFieldDefinition,
  form: MatchingFieldForm,
  translation: MatchingTranslation = clientTranslator,
  parent?: ChemistryLitElement,
) {
  switch (field.name) {
    case 'dateOfBirth':
      return stateful(() => {
        const [birthday, setBirthday] = useState<Date | null>(null);
        const age = birthday ? differenceInYears(new Date(), birthday) : null;

        console.log({ age });

        return html`
          <tr-input
            type="date"
            placeholder=${translation.translate('field', 'dateOfBirth')}
            .value=${form.document instanceof MatchingHautnahMenteeUser ? form.document.dateOfBirth : undefined}
            @value-picked=${(e: ValueEvent<Date | null>) => {
              setBirthday(e.detail.value);
              if (form.document instanceof MatchingHautnahMenteeUser) form.document.dateOfBirth = e.detail.value;
            }}
          ></tr-input>

          ${
            Number.isInteger(age) && age !== null && age < 16
              ? html`
                  <p>
                    als Schüler unter 16 Jahren must du hier die von deinen Eltern unterzeichnete
                    Einverständniserklärung hochladen:<br />
                    <a href=${einverstandiserklärung} target="_blank"
                      >Einverständniserklärung für die Teilnahme am Projekt.pdf</a
                    >
                  </p>
                  <tr-file-input ${form.field('parentalConsentDocID')} placeholder="Einverständniserklärung">
                  </tr-file-input>
                `
              : nothing
          }
        `;
      })();

    case 'numberOfFellowMentees':
      return html`<tr-dropdown-selection
        placeholder=${translation.translate('field', field.name)}
        .selectables=${[2, 4]}
        .renderString=${(value: number) => (value === 2 ? '1-2 Personen' : '3-4 Personen')}
        info=${translation.translate('field_description', field.name)}
        @value-picked=${(e: ValueEvent<number>) => {
          if (form.document instanceof MatchingHautnahMenteeUser) form.document.numberOfFellowMentees = e.detail.value;
        }}
      ></tr-dropdown-selection>`;

    case 'age':
    case 'parentalConsentDoc':
    case 'parentalConsentDocID':
      return html``;
    case 'dataPrivacyConsent':
      const trans = translation.translate('field', field.name);
      return html`
        <tr-checkbox
          .label=${html`${unsafeHTML(
            trans
              .replace('Datenschutzerklärung', '<a href="/data-privacy" target="_blank"> Datenschutzerklärung</a>')
              .replace('privacy agreement', '<a href="/data-privacy" target="_blank"> privacy agreement</a>'),
          )}`}
          ${form.field(field.name)}
          ${css({
            gridColumn: '1 / -1',
          })}
        ></tr-checkbox>
      `;
    default:
      return undefined;
  }
}
